import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider } from "../firebase";

// Client-side allowlist. Not a real security boundary (anyone can edit this
// in-browser) — the backend /api/v1/admin/* endpoints enforce the real check
// via Firebase ID token + server-side allowlist. This is just for UX (hiding
// the dashboard from someone who signs in with a wrong Google account).
const ADMIN_EMAILS = new Set([
  "developer@tomitra.com",
]);

interface AuthContextValue {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  token: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function checkAdmin(email: string | null): boolean {
  return !!email && ADMIN_EMAILS.has(email.toLowerCase());
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        setIsAdmin(checkAdmin(u.email));
        setToken(await u.getIdToken());
      } else {
        setIsAdmin(false);
        setToken(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Refresh token periodically (Firebase ID tokens expire after 1h)
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(async () => {
      setToken(await user.getIdToken(true));
    }, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const value: AuthContextValue = {
    user,
    isAdmin,
    loading,
    token,
    signIn: async () => {
      await signInWithPopup(auth, googleProvider);
    },
    signOut: async () => {
      await signOut(auth);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
